'use client';

import { useCallback, useState } from 'react';
import { useAuth } from '@/context/AuthProvider';
import { TOKEN_KEY } from '@/consts/const';
import type {
  DirectMediaUploadResponse,
  FinalizeMediaByFingerprintsResponse,
  MediaChunkSessionStatusResponse,
  MediaChunkUploadResponse,
  MediaStorageName,
  MediaUploadContext,
  MediaVisibility,
} from '@/types/mediaApiTypes';
import { DEFAULT_DIRECT_UPLOAD_MAX_BYTES, DEFAULT_MEDIA_CHUNK_BYTES } from './utils/constants';
import { getChunkPartName } from './utils/chunkPartName';
import { MediaUploadError } from './utils/mediaUploadError';
import { parseJsonResponse } from './utils/parseJsonResponse';
import { sendPreRequestAuditIfMatched } from '@/utils/audit/sendPreRequestAudit';
import { sha256Hex } from './utils/sha256Hex';

const MEDIA_API = '/api/media';
const MEDIA_CHUNKS_API = '/api/media/chunks';

function getApiBaseUrl(): string {
  return `${process.env.NEXT_PUBLIC_API_URL ?? ''}`;
}

function buildUploadContextHeaders(
  uploadContext: MediaUploadContext,
  storageName?: MediaStorageName
): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Upload-Context': uploadContext,
  };
  if (storageName) {
    headers['X-Storage-Name'] = storageName;
  }
  return headers;
}

async function fetchWithAuthRetry(
  input: string,
  init: RequestInit,
  refreshToken: () => void | Promise<void | boolean>
): Promise<Response> {
  const baseUrl = getApiBaseUrl();
  const url = input.startsWith('http') ? input : `${baseUrl}${input}`;

  const run = async (token: string | null) => {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (init.body instanceof FormData) {
      headers.delete('Content-Type');
    }
    const requestInit: RequestInit = { ...init, headers };
    await sendPreRequestAuditIfMatched(input, requestInit);
    return fetch(url, requestInit);
  };

  let token = localStorage.getItem(TOKEN_KEY);
  let response = await run(token);

  if (response.status === 401) {
    await Promise.resolve(refreshToken());
    token = localStorage.getItem(TOKEN_KEY);
    response = await run(token);
  }

  return response;
}

function toMediaUploadError(e: unknown): MediaUploadError {
  if (e instanceof MediaUploadError) return e;
  if (e instanceof DOMException && e.name === 'AbortError') {
    return new MediaUploadError(499, e.message);
  }
  return new MediaUploadError(0, e instanceof Error ? e.message : String(e));
}

export interface UploadFileInChunksOptions {
  file: File;
  fileFingerprint?: string;
  uploadContext: MediaUploadContext;
  visibility: MediaVisibility;
  storageName?: MediaStorageName;
  chunkSizeBytes?: number;
  signal?: AbortSignal;
  onProgress?: (loadedBytes: number, totalBytes: number) => void;
}

export interface UploadDirectFilesOptions {
  files: File[];
  uploadContext: MediaUploadContext;
  visibility: MediaVisibility;
  storageName?: MediaStorageName;
  signal?: AbortSignal;
}

export interface FinalizeChunkUploadsOptions {
  fingerprints: string[];
  signal?: AbortSignal;
}

export interface UseMediaUploadReturn {
  uploadFileInChunks: (options: UploadFileInChunksOptions) => Promise<MediaChunkUploadResponse>;
  finalizeChunkUploads: (
    options: FinalizeChunkUploadsOptions
  ) => Promise<FinalizeMediaByFingerprintsResponse>;
  getChunkSessionStatus: (fingerprint: string, signal?: AbortSignal) => Promise<MediaChunkSessionStatusResponse>;
  uploadDirectFiles: (options: UploadDirectFilesOptions) => Promise<DirectMediaUploadResponse>;
  uploadMediaAuto: (
    file: File,
    options: Omit<UploadFileInChunksOptions, 'file'> & {
      directMaxBytes?: number;
    }
  ) => Promise<DirectMediaUploadResponse | FinalizeMediaByFingerprintsResponse>;
  isUploading: boolean;
  error: MediaUploadError | null;
}

type Refresh = () => void | Promise<void | boolean>;

async function uploadFileInChunksCore(
  options: UploadFileInChunksOptions,
  refreshToken: Refresh
): Promise<MediaChunkUploadResponse> {
  const {
    file,
    fileFingerprint = crypto.randomUUID(),
    uploadContext,
    visibility,
    storageName,
    chunkSizeBytes = DEFAULT_MEDIA_CHUNK_BYTES,
    signal,
    onProgress,
  } = options;

  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSizeBytes));
  const rawFilename = file.name || 'upload.bin';
  const checksumSha256 = await sha256Hex(await file.arrayBuffer());

  let lastResponse: MediaChunkUploadResponse | null = null;

  for (let i = 1; i <= totalChunks; i++) {
    if (signal?.aborted) {
      throw new DOMException('Upload aborted', 'AbortError');
    }

    const start = (i - 1) * chunkSizeBytes;
    const end = Math.min(start + chunkSizeBytes, file.size);
    const blob = file.slice(start, end);

    const form = new FormData();
    form.append('chunk', blob, getChunkPartName(i));
    form.append('file_fingerprint', fileFingerprint);
    form.append('current_chunk', String(i));
    form.append('total_chunks', String(totalChunks));
    form.append('chunk_size_bytes', String(chunkSizeBytes));
    form.append('raw_filename', rawFilename);
    form.append('visibility', visibility);
    form.append('checksum_sha256', checksumSha256);
    form.append('total_bytes', String(file.size));

    const response = await fetchWithAuthRetry(
      MEDIA_CHUNKS_API,
      {
        method: 'POST',
        body: form,
        headers: buildUploadContextHeaders(uploadContext, storageName),
        signal,
      },
      refreshToken
    );

    const json = await parseJsonResponse<MediaChunkUploadResponse>(response);
    lastResponse = json;

    const loaded = Math.min(end, file.size);
    onProgress?.(loaded, file.size);

    if (json.data.complete) {
      return json;
    }
  }

  if (lastResponse?.data.complete) {
    return lastResponse;
  }

  throw new Error('Chunk upload ended without completion from the server');
}

async function finalizeChunkUploadsCore(
  options: FinalizeChunkUploadsOptions,
  refreshToken: Refresh
): Promise<FinalizeMediaByFingerprintsResponse> {
  const { fingerprints, signal } = options;
  const form = new FormData();
  fingerprints.forEach((fp) => {
    form.append('upload_by_fingerprints[]', fp);
  });

  const response = await fetchWithAuthRetry(
    MEDIA_API,
    {
      method: 'POST',
      body: form,
      headers: { Accept: 'application/json' },
      signal,
    },
    refreshToken
  );

  return parseJsonResponse<FinalizeMediaByFingerprintsResponse>(response);
}

async function getChunkSessionStatusCore(
  fingerprint: string,
  signal: AbortSignal | undefined,
  refreshToken: Refresh
): Promise<MediaChunkSessionStatusResponse> {
  const response = await fetchWithAuthRetry(
    `${MEDIA_CHUNKS_API}/${encodeURIComponent(fingerprint)}`,
    {
      method: 'POST',
      headers: { Accept: 'application/json' },
      signal,
    },
    refreshToken
  );

  return parseJsonResponse<MediaChunkSessionStatusResponse>(response);
}

async function uploadDirectFilesCore(
  options: UploadDirectFilesOptions,
  refreshToken: Refresh
): Promise<DirectMediaUploadResponse> {
  const { files, uploadContext, visibility, storageName, signal } = options;
  const form = new FormData();
  files.forEach((f) => form.append('files[]', f));
  form.append('visibility', visibility);

  const response = await fetchWithAuthRetry(
    `${MEDIA_API}/`,
    {
      method: 'POST',
      body: form,
      headers: buildUploadContextHeaders(uploadContext, storageName),
      signal,
    },
    refreshToken
  );

  return parseJsonResponse<DirectMediaUploadResponse>(response);
}

export function useMediaUpload(): UseMediaUploadReturn {
  const { refreshToken } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<MediaUploadError | null>(null);

  const runWithLoading = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setError(null);
    setIsUploading(true);
    try {
      return await fn();
    } catch (e) {
      const mapped = toMediaUploadError(e);
      setError(mapped);
      throw e;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const uploadFileInChunks = useCallback(
    async (options: UploadFileInChunksOptions) => {
      return runWithLoading(() => uploadFileInChunksCore(options, refreshToken));
    },
    [refreshToken, runWithLoading]
  );

  const finalizeChunkUploads = useCallback(
    async (options: FinalizeChunkUploadsOptions) => {
      return runWithLoading(() => finalizeChunkUploadsCore(options, refreshToken));
    },
    [refreshToken, runWithLoading]
  );

  const getChunkSessionStatus = useCallback(
    async (fingerprint: string, signal?: AbortSignal) => {
      return runWithLoading(() => getChunkSessionStatusCore(fingerprint, signal, refreshToken));
    },
    [refreshToken, runWithLoading]
  );

  const uploadDirectFiles = useCallback(
    async (options: UploadDirectFilesOptions) => {
      return runWithLoading(() => uploadDirectFilesCore(options, refreshToken));
    },
    [refreshToken, runWithLoading]
  );

  const uploadMediaAuto = useCallback(
    async (
      file: File,
      options: Omit<UploadFileInChunksOptions, 'file'> & { directMaxBytes?: number }
    ): Promise<DirectMediaUploadResponse | FinalizeMediaByFingerprintsResponse> => {
      setError(null);
      setIsUploading(true);
      try {
        const { directMaxBytes = DEFAULT_DIRECT_UPLOAD_MAX_BYTES, ...chunkOptions } = options;

        if (file.size <= directMaxBytes) {
          return await uploadDirectFilesCore(
            {
              files: [file],
              uploadContext: chunkOptions.uploadContext,
              visibility: chunkOptions.visibility,
              storageName: chunkOptions.storageName,
              signal: chunkOptions.signal,
            },
            refreshToken
          );
        }

        const fingerprint = chunkOptions.fileFingerprint ?? crypto.randomUUID();

        await uploadFileInChunksCore(
          {
            ...chunkOptions,
            file,
            fileFingerprint: fingerprint,
          },
          refreshToken
        );

        return await finalizeChunkUploadsCore(
          {
            fingerprints: [fingerprint],
            signal: chunkOptions.signal,
          },
          refreshToken
        );
      } catch (e) {
        const mapped = toMediaUploadError(e);
        setError(mapped);
        throw e;
      } finally {
        setIsUploading(false);
      }
    },
    [refreshToken]
  );

  return {
    uploadFileInChunks,
    finalizeChunkUploads,
    getChunkSessionStatus,
    uploadDirectFiles,
    uploadMediaAuto,
    isUploading,
    error,
  };
}
